import { lazy, useState, type ReactNode } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useCustomerAppConfig } from "undercarriage";

const INTELLIGENCE_NAV_ITEM_ID = "intelligence page title";

interface ActivationState {
  activated: boolean;
  threadId: string | undefined;
}

const NOT_ACTIVATED: ActivationState = { activated: false, threadId: undefined };

// Latches true on the first Intelligence visit and stays true for the session, so the chat
// runtime (hosted above the router) mounts once and survives later navigation. threadId is
// captured only on that one latching render, as the seed for the runtime's initial thread —
// it must never recompute on later renders (e.g. once the user navigates away and the URL no
// longer matches), since assistant-ui treats a changed threadId prop as a live "switch thread"
// command, not just an initial value.
const useIntelligenceActivated = (): ActivationState => {
  const { data: appConfig } = useCustomerAppConfig();
  const intelligenceEnabled = appConfig?.navItems?.some((item) => item.id === INTELLIGENCE_NAV_ITEM_ID);
  const { pathname, search } = useLocation();
  const [state, setState] = useState(NOT_ACTIVATED);

  if (!state.activated && intelligenceEnabled && appConfig.customerUuid && /\/intelligence(\/|$)/.test(pathname)) {
    // A bare /intelligence visit (no URL threadId) resumes the tab's last thread instead of
    // starting a new chat; ThreadIdSync (in the intelligence package) is what keeps this key
    // up to date and corrects the URL to match once the runtime resumes it.
    const isPromptDeepLink = (new URLSearchParams(search).get("prompt") ?? "").trim().length > 0;
    setState({
      activated: true,
      threadId:
        matchPath("/:customerDomain/intelligence/:threadId", pathname)?.params.threadId ??
        (isPromptDeepLink
          ? undefined
          : sessionStorage.getItem(`intelligence-last-thread-${appConfig.customerDomain}`) ?? undefined),
    });
  }

  return state;
};

// Lazy so the chat runtime and its assistant-ui dependencies only enter the bundle for customers
// who actually open Intelligence — everyone else keeps the smaller main bundle.
const IntelligenceRuntimeProvider = lazy(() =>
  import("intelligence").then((module) => ({ default: module.IntelligenceRuntimeProvider }))
);

// Wraps children in the chat runtime once Intelligence has been activated, keeping them mounted
// as-is otherwise. `activated` only latches once `customerUuid` is present, so the cast is safe.
export const IntelligenceRuntimeGate = ({ children }: { children: ReactNode }) => {
  const { activated, threadId } = useIntelligenceActivated();
  const { data: appConfig } = useCustomerAppConfig();

  if (!activated) {
    return children;
  }

  return (
    <IntelligenceRuntimeProvider
      companyUuid={appConfig.customerUuid as string}
      customerDomain={appConfig.customerDomain}
      threadId={threadId}
    >
      {children}
    </IntelligenceRuntimeProvider>
  );
};
